import React, { useEffect, useState } from 'react'

import Checkbox from '@instabox/internal-web-ui/components/Checkbox'
import Input from '@instabox/internal-web-ui/components/Input'
import Table from '@instabox/internal-web-ui/components/Table'
import CheckIcon from '@instabox/internal-web-ui/icons/CheckIcon'
import { Device } from '@instabox/lockers-update-common/types'
import { orderBy } from 'lodash'

import useDevices from '../../hooks/useDevices'
import TableHeaderButton from '../components/TableHeaderButton'

import styles from './RolloutDeviceTable.module.scss'

interface RolloutDeviceTableProps {
  onSelectedDevicesChanged: (deviceIds: string[]) => void
  targetDevices: string[]
  loading: boolean
}

const RolloutDeviceTable: React.FC<RolloutDeviceTableProps> = ({
  onSelectedDevicesChanged,
  targetDevices,
  loading: rolloutLoading,
}) => {
  const { devices, loading: devicesLoading } = useDevices()
  const [localDevices, setLocalDevices] = useState<Device[]>([])

  const handleSortByPartOfRolloutClicked = () => {
    const sorted = orderBy(localDevices, (d) => targetDevices.includes(d.deviceId) === false)

    if (JSON.stringify(sorted) === JSON.stringify(localDevices)) {
      setLocalDevices(sorted.reverse())
      return
    }

    setLocalDevices(sorted)
  }

  const handleHeaderClicked = (sortingKey: keyof Device) => {
    const sorted = orderBy(localDevices, (device) => device[sortingKey])

    if (JSON.stringify(sorted) === JSON.stringify(localDevices)) {
      setLocalDevices(sorted.reverse())
      return
    }

    setLocalDevices(sorted)
  }

  const handleDeviceFilterChanged = (filter: string) => {
    const deviceFilters = filter
      .replace(/,/g, ' ')
      .split(' ')
      .filter((e) => e.length)

    setLocalDevices(
      devices.filter(
        (device) =>
          deviceFilters.length === 0 ||
          deviceFilters.some((filterName) => device.deviceName.toUpperCase().includes(filterName.toUpperCase()))
      )
    )
  }

  const handleDeviceSelected = (deviceId: string) => {
    if (targetDevices.includes(deviceId)) {
      onSelectedDevicesChanged(targetDevices.filter((d) => d !== deviceId))
      return
    }

    onSelectedDevicesChanged([...targetDevices, deviceId])
  }

  const handleSelectAllDevices = (checked: boolean) => {
    if (checked) {
      const checked = new Set<string>([...targetDevices, ...localDevices.map((d) => d.deviceId)])
      onSelectedDevicesChanged(Array.from(checked))
      return
    }

    onSelectedDevicesChanged(targetDevices.filter((d) => localDevices.some((ld) => ld.deviceId === d) === false))
  }

  useEffect(() => {
    setLocalDevices(devices)
  }, [devices])

  return (
    <div className={styles.table}>
      <Table
        isLoading={devicesLoading || rolloutLoading}
        headers={{
          checkbox: (
            <div>
              <TableHeaderButton
                data-testid="partOfRolloutHeader"
                label="Part of Rollout"
                onClick={() => handleSortByPartOfRolloutClicked()}
              />
              <Checkbox
                id="selectAllCheckbox"
                data-testid="selectAllCheckbox"
                className="mt-2"
                checked={localDevices.every((d) => targetDevices.includes(d.deviceId))}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSelectAllDevices(e.target.checked)}
              />
            </div>
          ),
          favorite: (
            <TableHeaderButton
              data-testid="favoriteHeader"
              label="Favorite"
              onClick={() => handleHeaderClicked('favorite')}
            />
          ),
          deviceName: (
            <div>
              <TableHeaderButton
                data-testid="deviceNameHeader"
                label="Device Name"
                onClick={() => handleHeaderClicked('deviceName')}
              />
              <Input
                id="filterDevices"
                placeholder="filter"
                data-testid="filterDevicesInput"
                className="mt-2"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleDeviceFilterChanged(e.target.value)}
              />
            </div>
          ),
          deviceId: (
            <TableHeaderButton
              data-testid="deviceIdHeader"
              label="Device Id"
              onClick={() => handleHeaderClicked('deviceId')}
            />
          ),
        }}
        rows={localDevices.map((device) => ({
          ...device,
          checkbox: (
            <Checkbox
              id={`${device.deviceId}-selectCheckbox`}
              data-testid={`${device.deviceId}-selectCheckbox`}
              checked={targetDevices.includes(device.deviceId)}
              onChange={() => handleDeviceSelected(device.deviceId)}
            />
          ),
          favorite: device.favorite ? <CheckIcon /> : '',
        }))}
      />
    </div>
  )
}

export default RolloutDeviceTable
